import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useClients } from "@/contexts/ClientContext";
import { Card, CardContent } from "@/components/ui/card";
import IntegrationCapabilities from "@/components/IntegrationCapabilities";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Wallet, KeyRound, Eye, EyeOff, Save, Trash2, Loader2,
  CheckCircle2, AlertTriangle, RefreshCw, Users, Calendar, FileText,
} from "lucide-react";

/**
 * KeyPay / Employment Hero Payroll — per-client API key management.
 *
 * Admins onboard each agency client by pasting that client's KeyPay
 * API key + business_id. Iceberg never uses its own master KeyPay key
 * here — each client's key is stored encrypted and used only when
 * pulling that client's payroll data.
 */
export default function KeyPayPage() {
  const { clients } = useClients();
  const [configured, setConfigured] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(false);
  // form
  const [apiKey, setApiKey] = useState("");
  const [businessId, setBusinessId] = useState("");
  const [region, setRegion] = useState("AU");
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const loadList = useCallback(async () => {
    try {
      const { data } = await api.get("/integrations/keypay/clients");
      setConfigured(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load KeyPay configs");
    }
  }, []);
  useEffect(() => { loadList(); }, [loadList]);

  const loadCfg = useCallback(async (cid) => {
    if (!cid) { setCfg(null); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/integrations/keypay/clients/${cid}`);
      setCfg(data);
      setBusinessId(data.business_id ? String(data.business_id) : "");
      setRegion(data.region || "AU");
      setApiKey("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load config");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { loadCfg(selectedClientId); }, [selectedClientId, loadCfg]);

  const save = async () => {
    if (!selectedClientId) { toast.error("Pick a client first."); return; }
    if (!apiKey.trim() && !cfg?.has_api_key) {
      toast.error("Paste the client's KeyPay API key."); return;
    }
    if (!businessId.trim()) { toast.error("Business ID required."); return; }
    setSaving(true);
    try {
      const body = { business_id: Number(businessId), region };
      if (apiKey.trim()) body.api_key = apiKey.trim();
      const { data } = await api.put(`/integrations/keypay/clients/${selectedClientId}`, body);
      // The PUT now runs a /business/{id} test inline. Surface the result.
      if (data.test_ok) {
        toast.success(`Saved & verified${data.business_name ? ` · ${data.business_name}` : ""}`);
      } else {
        toast.warning(`Saved but test failed: ${data.test_error || "unknown"}`,
                      { duration: 10000 });
      }
      setApiKey("");
      await Promise.all([loadList(), loadCfg(selectedClientId)]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!selectedClientId) return;
    if (!window.confirm("Remove KeyPay credentials for this client?")) return;
    try {
      await api.delete(`/integrations/keypay/clients/${selectedClientId}`);
      toast.success("Removed");
      await Promise.all([loadList(), loadCfg(selectedClientId)]);
    } catch (e) { toast.error(e.response?.data?.detail || "Remove failed"); }
  };

  const test = async () => {
    if (!selectedClientId) return;
    setTesting(true);
    try {
      const { data } = await api.post(`/integrations/keypay/clients/${selectedClientId}/test`);
      toast.success(`Connected to ${data.business_name || "business"}`);
      await loadCfg(selectedClientId);
    } catch (e) { toast.error(e.response?.data?.detail || "Test failed"); }
    finally { setTesting(false); }
  };

  const clientById = (cid) => clients.find((c) => c.client_id === cid);

  return (
    <div className="space-y-6 p-6 max-w-6xl" data-testid="keypay-page">
      <div className="flex items-center gap-3">
        <div className="h-12 w-12 grid place-items-center rounded-sm bg-[#FF6B35]/10">
          <Wallet className="h-6 w-6 text-[#FF6B35]" />
        </div>
        <div>
          <div className="label-overline">KEYPAY · EMPLOYMENT HERO PAYROLL</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight">KeyPay</h1>
          <p className="text-sm text-zinc-500 max-w-3xl mt-1">
            Per-client integration. Each client supplies their own KeyPay (Employment Hero Payroll)
            API key &amp; business ID, which we use to pull employees, pay runs and leave for their reports.
            Rate limit: 5 req/sec/key.
          </p>
        </div>
      </div>

      {/* ── Cross-client rollup link (mirrors Xero rollup pattern) ── */}
      <Card className="rounded-sm border-zinc-200 bg-gradient-to-br from-[#FF6B35]/5 to-white">
        <CardContent className="p-5 flex items-center gap-4">
          <div className="h-10 w-10 grid place-items-center rounded-sm bg-[#FF6B35] text-white shrink-0">
            <Wallet className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="label-overline">KEYPAY · CROSS-CLIENT ROLLUP</div>
            <div className="font-display font-bold text-lg">All payroll clients</div>
            <p className="text-sm text-zinc-500 mt-0.5">
              Total active employees, last-30-day payroll spend, pending leave &amp; timesheets across every KeyPay-connected client.
              Includes the cross-client birthdays &amp; anniversaries widget. Cached &amp; refreshed nightly @ 07:00 Sydney.
            </p>
          </div>
          <Link to="/integrations/keypay/rollup" data-testid="keypay-rollup-link">
            <Button size="sm" className="rounded-sm bg-[#FF6B35] hover:bg-[#FF6B35]/90 text-white">
              Open rollup →
            </Button>
          </Link>
        </CardContent>
      </Card>

      {/* ── Configured-clients list ── */}
      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-3">
            <div className="font-display font-bold text-lg">Configured clients ({configured.length})</div>
            <Button size="sm" variant="ghost" className="rounded-sm" onClick={loadList}
              data-testid="keypay-reload">
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
            </Button>
          </div>
          {configured.length === 0 ? (
            <div className="text-sm italic text-zinc-500">No clients have KeyPay configured yet.</div>
          ) : (
            <div className="border border-zinc-200 rounded-sm overflow-hidden text-sm">
              <div className="grid grid-cols-[1.5fr_1fr_0.6fr_1fr_auto] gap-2 px-3 py-2 bg-zinc-50 border-b border-zinc-200 font-display font-bold text-xs">
                <span>Client</span><span>Business ID</span><span>Region</span><span>Last test</span><span></span>
              </div>
              {configured.map((c) => {
                const cl = clientById(c.client_id);
                return (
                  <div key={c.client_id}
                    className="grid grid-cols-[1.5fr_1fr_0.6fr_1fr_auto] gap-2 px-3 py-2 border-b border-zinc-100 last:border-b-0 items-center"
                    data-testid={`keypay-row-${c.client_id}`}>
                    <span className="truncate">{cl?.company_name || c.client_id}</span>
                    <span className="font-mono text-[11px]">{c.business_id}</span>
                    <span className="font-mono text-[11px]">{c.region}</span>
                    <span className="text-[11px]">
                      {c.last_test_at
                        ? (c.last_test_ok
                          ? <span className="text-emerald-700 inline-flex items-center"><CheckCircle2 className="h-3 w-3 mr-1" />ok</span>
                          : <span className="text-red-700 inline-flex items-center" title={c.last_test_error || ""}><AlertTriangle className="h-3 w-3 mr-1" />error</span>)
                        : <span className="text-zinc-400">never</span>}
                    </span>
                    <Link to={`/integrations/keypay/clients/${c.client_id}`}
                      className="inline-flex items-center text-xs text-purple-700 font-semibold hover:underline mr-2"
                      data-testid={`keypay-dashboard-${c.client_id}`}>
                      Dashboard →
                    </Link>
                    <Button size="sm" variant="ghost" className="rounded-sm text-xs h-7"
                      onClick={() => setSelectedClientId(c.client_id)}
                      data-testid={`keypay-edit-${c.client_id}`}>Edit</Button>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Add / edit per-client config ── */}
      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-6 space-y-4">
          <div className="font-display font-bold text-lg flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-zinc-500" />
            {selectedClientId && cfg?.configured ? "Edit" : "Add"} KeyPay credentials
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="text-xs uppercase tracking-[0.18em] text-zinc-500">
              Client
              <select value={selectedClientId}
                onChange={(e) => setSelectedClientId(e.target.value)}
                data-testid="keypay-client-select"
                className="mt-1.5 w-full h-10 border border-zinc-200 rounded-sm px-2 text-sm bg-white">
                <option value="">— Pick a client —</option>
                {clients.map((c) => (
                  <option key={c.client_id} value={c.client_id}>{c.company_name}</option>
                ))}
              </select>
            </label>
            <label className="text-xs uppercase tracking-[0.18em] text-zinc-500">
              Region
              <select value={region} onChange={(e) => setRegion(e.target.value)}
                data-testid="keypay-region"
                className="mt-1.5 w-full h-10 border border-zinc-200 rounded-sm px-2 text-sm bg-white">
                <option value="AU">Australia</option>
                <option value="NZ">New Zealand</option>
                <option value="UK">United Kingdom</option>
                <option value="SG">Singapore</option>
              </select>
            </label>
            <label className="text-xs uppercase tracking-[0.18em] text-zinc-500">
              Business ID
              <Input value={businessId} onChange={(e) => setBusinessId(e.target.value)}
                type="number" placeholder="e.g. 123456"
                className="mt-1.5 font-mono text-sm" data-testid="keypay-business-id" />
            </label>
            <label className="text-xs uppercase tracking-[0.18em] text-zinc-500">
              API key {cfg?.has_api_key && <span className="text-emerald-600 normal-case tracking-normal">(already saved · paste a new one to replace)</span>}
              <div className="relative mt-1.5">
                <Input value={apiKey} onChange={(e) => setApiKey(e.target.value)}
                  type={showKey ? "text" : "password"}
                  placeholder={cfg?.has_api_key ? "•••• (leave blank to keep)" : "Paste KeyPay API key"}
                  className="font-mono text-sm pr-9" data-testid="keypay-api-key" />
                <button type="button" onClick={() => setShowKey((s) => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700"
                  data-testid="keypay-toggle-key">
                  {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </label>
          </div>
          <p className="text-[11px] text-zinc-500">
            Generate the key inside KeyPay → My Account → API Keys. Regenerating invalidates the old one.
          </p>
          <div className="flex flex-wrap gap-2 pt-1">
            <Button size="sm" className="rounded-sm bg-[#FF6B35] hover:bg-[#E55A24] text-white"
              disabled={!selectedClientId || saving} onClick={save}
              data-testid="keypay-save">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                : <Save className="h-3.5 w-3.5 mr-1.5" />}
              Save
            </Button>
            {cfg?.configured && (
              <>
                <Button size="sm" variant="outline" className="rounded-sm"
                  disabled={testing} onClick={test} data-testid="keypay-test">
                  {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                    : <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />}
                  Test connection
                </Button>
                <Button size="sm" variant="ghost" className="rounded-sm text-red-600"
                  onClick={remove} data-testid="keypay-remove">
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Remove
                </Button>
              </>
            )}
          </div>
          {loading && <Loader2 className="h-4 w-4 animate-spin text-zinc-400" />}
        </CardContent>
      </Card>

      {/* ── Quick data peek for the selected client ── */}
      {selectedClientId && cfg?.configured && cfg?.last_test_ok && (
        <PreviewSection clientId={selectedClientId} />
      )}

      <IntegrationCapabilities integrationKey="keypay" accent="#FF6B35" />
    </div>
  );
}

function PreviewSection({ clientId }) {
  const [employees, setEmployees] = useState(null);
  const [payruns, setPayruns] = useState(null);
  const [leave, setLeave] = useState(null);
  const [loading, setLoading] = useState("");

  const fetchOne = async (kind) => {
    setLoading(kind);
    try {
      const url = ({
        employees: `/integrations/keypay/clients/${clientId}/employees`,
        payruns: `/integrations/keypay/clients/${clientId}/payruns`,
        leave: `/integrations/keypay/clients/${clientId}/leave-requests`,
      })[kind];
      const { data } = await api.get(url);
      if (kind === "employees") setEmployees(data);
      if (kind === "payruns") setPayruns(data);
      if (kind === "leave") setLeave(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Fetch failed"); }
    finally { setLoading(""); }
  };

  return (
    <Card className="rounded-sm border-zinc-200" data-testid="keypay-data-preview">
      <CardContent className="p-6 space-y-3">
        <div className="font-display font-bold text-lg">Data preview</div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" className="rounded-sm"
            disabled={loading === "employees"} onClick={() => fetchOne("employees")}
            data-testid="keypay-load-employees">
            {loading === "employees" ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
              : <Users className="h-3.5 w-3.5 mr-1.5" />}
            Load employees
          </Button>
          <Button size="sm" variant="outline" className="rounded-sm"
            disabled={loading === "payruns"} onClick={() => fetchOne("payruns")}
            data-testid="keypay-load-payruns">
            {loading === "payruns" ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
              : <FileText className="h-3.5 w-3.5 mr-1.5" />}
            Load last 20 pay runs
          </Button>
          <Button size="sm" variant="outline" className="rounded-sm"
            disabled={loading === "leave"} onClick={() => fetchOne("leave")}
            data-testid="keypay-load-leave">
            {loading === "leave" ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
              : <Calendar className="h-3.5 w-3.5 mr-1.5" />}
            Load leave requests
          </Button>
        </div>
        {employees && (
          <div>
            <Badge className="rounded-sm">{employees.count} employees</Badge>
            <pre className="mt-2 max-h-64 overflow-auto bg-zinc-50 border border-zinc-200 rounded-sm p-2 text-[10px] font-mono">
              {JSON.stringify((employees.employees || []).slice(0, 10), null, 2)}
            </pre>
          </div>
        )}
        {payruns && (
          <div>
            <Badge className="rounded-sm">{payruns.count} pay runs</Badge>
            <pre className="mt-2 max-h-64 overflow-auto bg-zinc-50 border border-zinc-200 rounded-sm p-2 text-[10px] font-mono">
              {JSON.stringify((payruns.payruns || []).slice(0, 10), null, 2)}
            </pre>
          </div>
        )}
        {leave && (
          <div>
            <Badge className="rounded-sm">{leave.count} leave requests</Badge>
            <pre className="mt-2 max-h-64 overflow-auto bg-zinc-50 border border-zinc-200 rounded-sm p-2 text-[10px] font-mono">
              {JSON.stringify((leave.leave_requests || []).slice(0, 10), null, 2)}
            </pre>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
